// A card the store takes with no price and prices when it checks the order in
// (TKT-2026-108714). gameSettings[categoryId].takeUnpricedCards, per game and off
// unless it is exactly true. The line goes in at 0 and staff price it on the order
// with Override Prices.
//
// This only keeps the kiosk's button and the order in agreement. buylist-api decides
// for real at submit (awaitsStorePrice, sortswift-buylist-api
// src/utils/buylistAwaitingStorePrice.js) from its own re-quote and ignores any flag a
// client sends. The buylist site's half is takesCardWithNoPrice in sortswift-buylist
// src/utils/buylistStoreRefusal.js. Keep all three the same shape.
//
// This reads every switch the API reads, including the game, singles and language
// ones, because a line it opens that the API does not stamp books at an unflagged 0.
// Languages fold through cardArt's normalizeLang, whose table
// backend/test/cardArt.portPin.test.js holds to the served language list.
import { normalizeLang } from './cardArt';

export const AWAITING_STORE_PRICE_CAPTION = 'The store prices this when it checks your order in';

const DAY_MS = 24 * 60 * 60 * 1000;

const CONDITION_CAMEL_CASE = {
    NM: 'nearMint',
    LP: 'lightlyPlayed',
    MP: 'moderatelyPlayed',
    HP: 'heavilyPlayed',
    DM: 'damaged',
};

// A flagged release-window bracket is only a refusal when it pays nothing on both legs.
function isAvailabilityEmbargo(quote) {
    if (quote.availabilityDelayApplied !== true) return false;
    return !(Number(quote.availabilityDelayCashModifier) > 0) && !(Number(quote.availabilityDelayCreditModifier) > 0);
}

function isDeliberateRefusal(quote) {
    return isAvailabilityEmbargo(quote) || quote.stockLimitReached === true || quote.notBuying === true;
}

function isBeforeBuyingDate(quote, now) {
    if (!quote.availableAfter) return false;
    const after = new Date(quote.availableAfter).getTime();
    return Number.isFinite(after) && after + DAY_MS > now;
}

// Read as the engine reads it: the spelling the config page shows wins, and a tick on
// any other spelling of that language accepts when the shown one is absent.
function isLanguageAccepted(languages, language) {
    if (!languages || typeof languages !== 'object') return false;
    const want = normalizeLang(language);
    if (!want) return false;
    let folded = false;
    for (const [key, value] of Object.entries(languages)) {
        if (normalizeLang(key) !== want) continue;
        if (String(key).trim().toUpperCase() === want) return Boolean(value);
        folded = folded || Boolean(value);
    }
    return folded;
}

/**
 * @param {object|null} quote the answer asked at this condition (quoteForCondition)
 * @param {object|null} gameBlock the store's block for the card's game: gameSettings[categoryId]
 * @param {{condition: string, language: string, sealed: boolean}} line
 * @returns {boolean}
 */
export function takesCardWithNoPrice(quote, gameBlock, { condition, language, sealed } = {}, now = Date.now()) {
    if (!gameBlock || gameBlock.takeUnpricedCards !== true) return false;
    if (!gameBlock.enabled || gameBlock.enableSingles === false) return false;
    if (sealed || !condition || !quote) return false;

    if (isDeliberateRefusal(quote) || quote.byAppointment || isBeforeBuyingDate(quote, now)) return false;
    // A zero under a card that HAS a reference price is the store's own rules at work,
    // and an answer with none (the early not-buying returns) is not "no price yet".
    if (quote.referencePrice === undefined || quote.referencePrice === null) return false;
    if (Number(quote.referencePrice) > 0) return false;

    const conditions = gameBlock.conditions || {};
    if (conditions[condition] !== true && conditions[CONDITION_CAMEL_CASE[condition]] !== true) return false;
    return isLanguageAccepted(gameBlock.languages, language || 'EN');
}
